import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { ClientLegalEntities } from '../components/ClientLegalEntities';
import { ClientDeliveryPoints } from '../components/ClientDeliveryPoints';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, Building2, UserCircle, Search, Plus,
  Pencil, Tag, Percent, Eye, LogIn, Crown, Ban, CheckCircle,
  ShoppingCart, TrendingUp, Calendar, MoreHorizontal, RefreshCw,
  Clock, Mail, Phone, MapPin, History, Network, Key, Lock, Unlock,
  ChevronDown, ChevronRight, UserPlus, Shield, Hash, Trash2, X,
  Upload, Download, FileDown, FileSpreadsheet, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import LanguageSwitcher from '../components/LanguageSwitcher';

function AdminClientsPro({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [filters, setFilters] = useState({
    status: 'all',
    clientType: 'all',
    groupId: 'all',
    search: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  
  const [showClientDetails, setShowClientDetails] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportBlocks, setExportBlocks] = useState({ clients: true, points: true, legal: true, managers: true });
  const [exportType, setExportType] = useState('all');
  const [exportActiveOnly, setExportActiveOnly] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  const [newClientForm, setNewClientForm] = useState({
    email: '',
    password: '',
    role: 'B2C',
    full_name: '',
    first_name: '',
    last_name: '',
    phone: '',
    company_name: '',
    tax_id: '',
    contact_person: '',
    address: '',
    client_group_id: '',
    discount_percentage: 0,
  });
  
  const [clientForm, setClientForm] = useState({
    company_name: '',
    full_name: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    contact_person: '',
    tax_id: '',
    discount_percentage: 0,
    is_active: true,
    client_group_id: '',
    price_tier: 'retail',
  });
  
  const [newStatus, setNewStatus] = useState({ status: 'active', notes: '' });

  // B2B Management state
  const [showB2BManagement, setShowB2BManagement] = useState(false);
  const [b2bData, setB2bData] = useState(null);
  const [b2bLoading, setB2bLoading] = useState(false);
  const [b2bOwnerId, setB2bOwnerId] = useState(null);
  const [expandedNetworks, setExpandedNetworks] = useState({});
  const [showCreateNetwork, setShowCreateNetwork] = useState(false);
  const [newNetworkForm, setNewNetworkForm] = useState({ name: '', description: '' });
  const [showCreateManager, setShowCreateManager] = useState(false);
  const [createManagerNetworkId, setCreateManagerNetworkId] = useState(null);
  const [newManagerForm, setNewManagerForm] = useState({ username: '', password: '', full_name: '', phone: '' });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  // Client password reset (any B2B/B2C client) — generate or set, then relay to client.
  const [showPwdReset, setShowPwdReset] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [pwdValue, setPwdValue] = useState('');
  const [pwdResult, setPwdResult] = useState(null);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [editCompanyCode, setEditCompanyCode] = useState(false);
  const [companyCodeValue, setCompanyCodeValue] = useState('');

  const CLIENT_STATUSES = [
    { value: 'active', label: 'Активный', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
    { value: 'vip', label: 'VIP', icon: Crown, color: 'bg-purple-100 text-purple-800' },
    { value: 'blocked', label: 'Заблокирован', icon: Ban, color: 'bg-red-100 text-red-800' },
  ];

  useEffect(() => {
    fetchData();
    fetchGroups();
  }, [page, filters]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder,
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.clientType !== 'all' && { client_type: filters.clientType }),
        ...(filters.groupId !== 'all' && { group_id: filters.groupId }),
        ...(filters.search && { search: filters.search }),
      });
      
      const response = await axios.get(`${API}/admin/clients/enhanced?${params}`, {
        headers: getAuthHeaders()
      });
      
      setClients(response.data.clients);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Не удалось загрузить клиентов');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/admin/client-groups`, { headers: getAuthHeaders() });
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups');
    }
  };

  const fetchClientDetails = async (clientId) => {
    try {
      const response = await axios.get(`${API}/admin/clients/${clientId}/details`, {
        headers: getAuthHeaders()
      });
      setClientDetails(response.data);
    } catch (error) {
      toast.error('Не удалось загрузить данные клиента');
    }
  };

  const handleViewClient = async (client) => {
    setSelectedClient(client);
    await fetchClientDetails(client.id);
    setShowClientDetails(true);
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setClientForm({
      company_name: client.company_name || '',
      full_name: client.full_name || '',
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      phone: client.phone || '',
      address: client.address || '',
      contact_person: client.contact_person || '',
      tax_id: client.tax_id || '',
      discount_percentage: client.discount_percentage || 0,
      is_active: client.is_active !== false,
      postpay_allowed: client.postpay_allowed === true,
      preferred_org_bin: client.preferred_org_bin || '',
      client_group_id: client.client_group_id || '',
      price_tier: client.price_tier || (client.role === 'B2B' ? 'wholesale' : 'retail'),
      delivery_priority: client.delivery_priority || 5,
      delivery_service_minutes: client.delivery_service_minutes || 10,
    });
    setShowEditClient(true);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    try {
      // Combine first_name and last_name into full_name for B2C
      const submitData = { ...clientForm };
      if (selectedClient.role === 'B2C' && (clientForm.first_name || clientForm.last_name)) {
        submitData.full_name = `${clientForm.first_name} ${clientForm.last_name}`.trim();
      }
      
      await axios.put(
        `${API}/admin/clients/${selectedClient.id}`,
        submitData,
        { headers: getAuthHeaders() }
      );
      toast.success('Клиент обновлён');
      setShowEditClient(false);
      fetchData();
    } catch (error) {
      toast.error('Не удалось сохранить');
    }
  };

  const handleCreateClient = async () => {
    if (creatingClient) return;
    try {
      if (!newClientForm.email || !newClientForm.password) {
        toast.error('Email и пароль обязательны');
        return;
      }
      setCreatingClient(true);
      await axios.post(
        `${API}/admin/clients`,
        {
          ...newClientForm,
          client_group_id: newClientForm.client_group_id || null,
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Клиент создан');
      setShowCreateClient(false);
      setNewClientForm({
        email: '', password: '', role: 'B2C', full_name: '', first_name: '', last_name: '',
        phone: '', company_name: '', tax_id: '', contact_person: '', address: '',
        client_group_id: '', discount_percentage: 0,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось создать клиента');
    } finally {
      setCreatingClient(false);
    }
  };


  const handleStatusChange = async () => {
    try {
      await axios.put(
        `${API}/admin/clients/${selectedClient.id}/status`,
        newStatus,
        { headers: getAuthHeaders() }
      );
      toast.success('Статус изменён');
      setShowStatusDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Не удалось изменить статус');
    }
  };

  // === Import/Export handlers ===
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients-io/template`, {
        headers: getAuthHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'b2b_clients_template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Шаблон скачан');
    } catch { toast.error('Не удалось скачать шаблон'); }
  };

  const handleExportClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients-io/export`, {
        headers: getAuthHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `b2b_clients_${new Date().toISOString().slice(0,10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Экспорт завершён');
    } catch { toast.error('Не удалось экспортировать'); }
  };

  const handleFullExport = async () => {
    const blocks = Object.keys(exportBlocks).filter(k => exportBlocks[k]);
    if (!blocks.length) { toast.error('Выберите хотя бы один блок'); return; }
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/admin/clients/full-export.xlsx?blocks=${blocks.join(',')}&client_type=${exportType}&active_only=${exportActiveOnly}`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients_full_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Экспорт готов');
      setShowExportDialog(false);
    } catch { toast.error('Не удалось экспортировать'); }
    setExporting(false);
  };

  const handleExportClientsCSV = async (clientType) => {
    try {
      const response = await axios.get(
        `${API}/admin/clients/export?client_type=${clientType}&active_only=true`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.download = `clients_${clientType}_${date}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Экспорт CSV (${clientType.toUpperCase()}) завершён`);
    } catch (e) { toast.error('Не удалось экспортировать CSV'); }
  };

  const handleImportClients = async () => {
    if (!importFile) { toast.error('Выберите файл'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await axios.post(`${API}/admin/clients-io/import`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      const d = response.data;
      if (d.owners_created > 0 || d.managers_created > 0) {
        const parts = [];
        if (d.owners_created > 0) parts.push(`${d.owners_created} клиентов`);
        if (d.networks_created > 0) parts.push(`${d.networks_created} сетей`);
        if (d.managers_created > 0) parts.push(`${d.managers_created} менеджеров`);
        toast.success(`Импортировано: ${parts.join(', ')}`);
        fetchData();
      }
      if (d.owners_skipped > 0 && d.owners_created === 0 && d.managers_created === 0) {
        toast.info('Все данные уже в системе — дубликатов не создано');
      }
      if (d.error_count > 0) {
        toast.error(`Ошибки: ${d.error_count} строк`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка импорта');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadErrors = async (reportId) => {    try {
      const response = await axios.get(`${API}/admin/clients-io/import-errors/${reportId}`, {
        headers: getAuthHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'client_import_errors.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Не удалось скачать отчёт'); }
  };

  const handleDownloadCleanupTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients-cleanup/template`, {
        headers: getAuthHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `b2b_cleanup_${new Date().toISOString().slice(0,10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Cleanup-шаблон скачан');
    } catch { toast.error('Не удалось скачать cleanup-шаблон'); }
  };

  const handleUploadCleanup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/clients-cleanup/import`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      const d = response.data;
      const parts = [];
      if (d.updated_contacts) parts.push(`${d.updated_contacts} контактов`);
      if (d.updated_legals) parts.push(`${d.updated_legals} юр.лиц`);
      toast.success(parts.length ? `Обновлено: ${parts.join(', ')}` : 'Изменений нет');
      if (d.errors?.length) toast.error(`Ошибки: ${d.errors.length}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка загрузки');
    }
  };

  const handleLoginAsClient = async (client) => {
    // Показываем loading
    const loadingToast = toast.loading(`Входим как ${client.email}...`);
    
    try {
      const response = await axios.post(
        `${API}/admin/clients/${client.id}/login-as`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Save current admin token for return
      const adminToken = localStorage.getItem('token');
      localStorage.setItem('admin_token_backup', adminToken);
      localStorage.setItem('admin_email_backup', user?.email || 'admin');
      
      // Set new token
      localStorage.setItem('token', response.data.token);
      
      toast.dismiss(loadingToast);
      toast.success(`Вы вошли как ${client.email}. Для возврата в админку нажмите кнопку в header.`);
      
      // Small delay to show toast, then navigate
      setTimeout(() => {
        // If B2B owner, redirect to their B2B dashboard
        if (client.role === 'B2B') {
          window.location.href = `/${currentLang}/b2b/networks`;
        } else {
          window.location.href = `/${currentLang}`;
        }
      }, 500);
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Login as client error:', error);
      toast.error('Не удалось войти как клиент: ' + (error.response?.data?.detail || error.message));
    }
  };

  // ==================== B2B MANAGEMENT HANDLERS ====================

  const fetchB2BHierarchy = async (ownerId) => {
    setB2bLoading(true);
    try {
      const response = await axios.get(`${API}/admin/b2b/${ownerId}/hierarchy`, { headers: getAuthHeaders() });
      setB2bData(response.data);
      setCompanyCodeValue(response.data.company_code || '');
    } catch (error) {
      toast.error('Не удалось загрузить B2B данные');
    } finally {
      setB2bLoading(false);
    }
  };

  const handleOpenB2BManagement = (client) => {
    setB2bOwnerId(client.id);
    setSelectedClient(client);
    setShowB2BManagement(true);
    fetchB2BHierarchy(client.id);
  };

  const handleUpdateCompanyCode = async () => {
    try {
      await axios.put(`${API}/admin/b2b/${b2bOwnerId}/company-code`, { company_code: companyCodeValue }, { headers: getAuthHeaders() });
      toast.success('Код компании обновлён');
      setEditCompanyCode(false);
      fetchB2BHierarchy(b2bOwnerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleAdminCreateNetwork = async () => {
    try {
      if (!newNetworkForm.name.trim()) { toast.error('Укажите название сети'); return; }
      await axios.post(`${API}/admin/b2b/${b2bOwnerId}/networks`, newNetworkForm, { headers: getAuthHeaders() });
      toast.success('Сеть создана');
      setShowCreateNetwork(false);
      setNewNetworkForm({ name: '', description: '' });
      fetchB2BHierarchy(b2bOwnerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleAdminDeleteNetwork = async (networkId) => {
    if (!window.confirm('Удалить сеть? Менеджеры будут отвязаны.')) return;
    try {
      await axios.delete(`${API}/admin/b2b/networks/${networkId}`, { headers: getAuthHeaders() });
      toast.success('Сеть удалена');
      fetchB2BHierarchy(b2bOwnerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleAdminCreateManager = async () => {
    try {
      if (!newManagerForm.username || !newManagerForm.password || !newManagerForm.full_name) {
        toast.error('Заполните логин, пароль и ФИО'); return;
      }
      await axios.post(`${API}/admin/b2b/networks/${createManagerNetworkId}/managers`, newManagerForm, { headers: getAuthHeaders() });
      toast.success('Менеджер создан');
      setShowCreateManager(false);
      setNewManagerForm({ username: '', password: '', full_name: '', phone: '' });
      fetchB2BHierarchy(b2bOwnerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleAdminDeleteManager = async (managerId) => {
    if (!window.confirm('Деактивировать менеджера?')) return;
    try {
      await axios.delete(`${API}/admin/b2b/managers/${managerId}`, { headers: getAuthHeaders() });
      toast.success('Менеджер деактивирован');
      fetchB2BHierarchy(b2bOwnerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleAdminSetAddressNetwork = async (addressId, networkId) => {
    try {
      await axios.put(
        `${API}/admin/b2b/addresses/${addressId}/network`,
        { network_id: networkId || null },
        { headers: getAuthHeaders() }
      );
      toast.success(networkId ? 'Адрес привязан к сети' : 'Адрес отвязан от сети');
      fetchB2BHierarchy(b2bOwnerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleAdminToggleNetworkStatus = async (network) => {
    const action = network.is_active ? 'закрыть' : 'снова открыть';
    const msg = network.is_active
      ? `Закрыть сеть «${network.name}»?\n\nЭто отключит:\n• Доставку на адреса этой сети\n• Логины менеджеров (${(network.managers || []).length} чел.)\n\nИстория и заказы сохранятся, всё можно вернуть.`
      : `Снова открыть сеть «${network.name}»?\n\nВернёт менеджеров и адреса в активное состояние.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.put(
        `${API}/admin/b2b/networks/${network.id}/status`,
        { is_active: !network.is_active },
        { headers: getAuthHeaders() }
      );
      toast.success(network.is_active ? '✓ Сеть закрыта' : '✓ Сеть снова активна');
      fetchB2BHierarchy(b2bOwnerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Не удалось ${action} сеть`);
    }
  };

  const handleAdminResetPassword = async () => {
    try {
      if (resetPasswordValue.length < 6) { toast.error('Минимум 6 символов'); return; }
      const url = resetPasswordTarget?.isOwner
        ? `${API}/admin/b2b/${resetPasswordTarget.id}/password`
        : `${API}/admin/b2b/managers/${resetPasswordTarget.id}/password`;
      await axios.put(url, { new_password: resetPasswordValue }, { headers: getAuthHeaders() });
      toast.success('Пароль сброшен');
      setShowResetPassword(false);
      setResetPasswordValue('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const toggleNetworkExpanded = (networkId) => {
    setExpandedNetworks(prev => ({ ...prev, [networkId]: !prev[networkId] }));
  };

  const openPwdReset = (client) => {
    setPwdTarget(client);
    setPwdValue('');
    setPwdResult(null);
    setShowPwdReset(true);
  };

  const submitPwdReset = async (generate) => {
    if (!pwdTarget) return;
    setPwdSaving(true);
    try {
      const payload = generate ? {} : { password: pwdValue };
      const r = await axios.post(`${API}/admin/clients/${pwdTarget.id}/reset-password`, payload, { headers: getAuthHeaders() });
      setPwdResult(r.data);
      toast.success('Пароль сброшен');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось сбросить пароль');
    } finally {
      setPwdSaving(false);
    }
  };

  const copyPwdCredentials = () => {
    if (!pwdResult) return;
    const txt = `Логин: ${pwdResult.email}\nПароль: ${pwdResult.password}`;
    navigator.clipboard?.writeText(txt);
    toast.success('Скопировано');
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount || 0) + ' ₸';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU');
  };

  const getStatusConfig = (status) => {
    return CLIENT_STATUSES.find(s => s.value === status) || CLIENT_STATUSES[0];
  };

  const getGroupName = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    return group?.name || '—';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">Загрузка клиентов...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`}>
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Назад
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Управление клиентами</h1>
                <p className="text-sm text-gray-500">Расширенное управление B2B и B2C клиентами</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => setShowCreateClient(true)} data-testid="create-client-btn" className="bg-emerald-700 hover:bg-emerald-800">
                <Plus className="w-4 h-4 mr-2" />
                Добавить клиента
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="import-export-clients-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Импорт / Экспорт
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowExportDialog(true)} data-testid="full-export-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Полный экспорт (Excel, все данные)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { setImportFile(null); setImportResult(null); setShowImportDialog(true); }} data-testid="import-clients-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Импорт клиентов
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportClients} data-testid="export-clients-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Экспорт B2B (Excel/xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-xs text-gray-500 font-medium">CSV (для Google Sheets / Excel)</div>
                  <DropdownMenuItem onClick={() => handleExportClientsCSV('all')} data-testid="export-csv-all-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Все клиенты — CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportClientsCSV('b2b')} data-testid="export-csv-b2b-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Только B2B — CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportClientsCSV('b2c')} data-testid="export-csv-b2c-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Только B2C — CSV
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleDownloadTemplate} data-testid="download-client-template-btn">
                    <FileDown className="w-4 h-4 mr-2" />
                    Скачать шаблон
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-xs text-amber-700 font-medium">Cleanup пробелов в B2B</div>
                  <DropdownMenuItem onClick={handleDownloadCleanupTemplate} data-testid="download-cleanup-template-btn">
                    <FileDown className="w-4 h-4 mr-2" />
                    Скачать пробелы (контакты + банк)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => document.getElementById('cleanup-upload-input')?.click()} data-testid="upload-cleanup-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Загрузить заполненный cleanup
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <input
                id="cleanup-upload-input"
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={handleUploadCleanup}
                data-testid="cleanup-upload-input"
              />
              <Link to={`/${currentLang}/admin/discount-groups`}>
                <Button variant="outline" data-testid="discount-groups-btn">
                  <Tag className="w-4 h-4 mr-2" />
                  Группы скидок
                </Button>
              </Link>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">Всего клиентов</div>
                  <div className="text-2xl font-bold">{total}</div>
                </div>
                <Users className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">B2B клиенты</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {clients.filter(c => c.role === 'B2B').length}
                  </div>
                </div>
                <Building2 className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">B2C клиенты</div>
                  <div className="text-2xl font-bold text-green-600">
                    {clients.filter(c => c.role === 'B2C').length}
                  </div>
                </div>
                <UserCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">VIP клиенты</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {clients.filter(c => c.client_status === 'vip').length}
                  </div>
                </div>
                <Crown className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm text-gray-600">Поиск</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Имя, email, телефон..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>

              <div className="w-[150px]">
                <Label className="text-sm text-gray-600">Статус</Label>
                <Select 
                  value={filters.status} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все статусы</SelectItem>
                    {CLIENT_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[120px]">
                <Label className="text-sm text-gray-600">Тип</Label>
                <Select 
                  value={filters.clientType} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, clientType: v }))}
                >
                  <SelectTrigger data-testid="type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="B2B">B2B</SelectItem>
                    <SelectItem value="B2C">B2C</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[180px]">
                <Label className="text-sm text-gray-600">Группа</Label>
                <Select 
                  value={filters.groupId} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, groupId: v }))}
                >
                  <SelectTrigger data-testid="group-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все группы</SelectItem>
                    <SelectItem value="none">Без группы</SelectItem>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[150px]">
                <Label className="text-sm text-gray-600">Сортировка</Label>
                <Select 
                  value={filters.sortBy} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, sortBy: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">По дате</SelectItem>
                    <SelectItem value="total_spent">По сумме</SelectItem>
                    <SelectItem value="order_count">По заказам</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({
                  status: 'all',
                  clientType: 'all',
                  groupId: 'all',
                  search: '',
                  sortBy: 'created_at',
                  sortOrder: 'desc'
                })}
                data-testid="reset-filters-btn"
              >
                Сбросить
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="clients-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Клиент</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Тип</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Статус</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Потрачено</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Заказы</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Последний</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Скидка</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Цены</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {clients.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-lg font-medium">Клиенты не найдены</p>
                      </td>
                    </tr>
                  ) : (
                    clients.map((client) => {
                      const statusConfig = getStatusConfig(client.client_status || 'active');
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr 
                          key={client.id} 
                          className={`hover:bg-gray-50 ${client.is_active === false ? 'opacity-50' : ''}`}
                          data-testid={`client-row-${client.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                client.role === 'B2B' ? 'bg-blue-100' : 'bg-green-100'
                              }`}>
                                {client.role === 'B2B' ? (
                                  <Building2 className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <UserCircle className="w-5 h-5 text-green-600" />
                                )}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {client.role === 'B2B' 
                                    ? (client.company_name || 'Без названия')
                                    : (client.first_name && client.last_name 
                                        ? `${client.first_name} ${client.last_name}` 
                                        : client.full_name || 'Без имени')
                                  }
                                </div>
                                <div className="text-sm text-gray-500">{client.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant={client.role === 'B2B' ? 'default' : 'secondary'}>
                              {client.role}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-semibold">{formatCurrency(client.total_spent)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="font-medium">{client.order_count || 0}</span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-500">
                            {formatDate(client.last_order_date)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {client.discount_percentage > 0 ? (
                              <span className="text-green-600 font-medium">{client.discount_percentage}%</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {client.price_tier === 'wholesale' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Опт
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Розн
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`client-actions-${client.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewClient(client)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Подробнее
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditClient(client)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Редактировать
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  setSelectedClient(client);
                                  setNewStatus({ status: client.client_status || 'active', notes: '' });
                                  setShowStatusDialog(true);
                                }}>
                                  <Tag className="w-4 h-4 mr-2" />
                                  Изменить статус
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {client.role === 'B2B' && (
                                  <DropdownMenuItem onClick={() => handleOpenB2BManagement(client)} data-testid={`b2b-manage-${client.id}`}>
                                    <Network className="w-4 h-4 mr-2 text-blue-600" />
                                    B2B Управление
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => openPwdReset(client)} data-testid={`reset-client-password-${client.id}`}>
                                  <Key className="w-4 h-4 mr-2 text-amber-600" />
                                  Сбросить пароль
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleLoginAsClient(client)}>
                                  <LogIn className="w-4 h-4 mr-2 text-purple-600" />
                                  Войти как клиент
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              Показано {clients.length} из {total} клиентов
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Назад
              </Button>
              <span className="px-3 py-1 text-sm">
                Страница {page} из {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Далее
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Client Details Dialog */}
      <Dialog open={showClientDetails} onOpenChange={setShowClientDetails}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Карточка клиента</DialogTitle>
          </DialogHeader>
          
          {clientDetails && (
            <div className="space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Клиент</Label>
                  <p className="text-lg font-semibold">
                    {clientDetails.client.role === 'B2B'
                      ? (clientDetails.client.company_name || 'Без названия')
                      : (clientDetails.client.first_name && clientDetails.client.last_name
                          ? `${clientDetails.client.first_name} ${clientDetails.client.last_name}`
                          : clientDetails.client.full_name || 'Без имени')
                    }
                  </p>
                  <Badge variant={clientDetails.client.role === 'B2B' ? 'default' : 'secondary'} className="mt-1">
                    {clientDetails.client.role}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Статус</Label>
                  <div className="mt-1">
                    <Badge className={getStatusConfig(clientDetails.client.client_status || 'active').color}>
                      {getStatusConfig(clientDetails.client.client_status || 'active').label}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{clientDetails.client.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{clientDetails.client.phone || '—'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{clientDetails.client.address || '—'}</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <ShoppingCart className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                    <div className="text-2xl font-bold">{clientDetails.stats.order_count}</div>
                    <div className="text-xs text-gray-500">Заказов</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <TrendingUp className="w-6 h-6 mx-auto mb-2 text-green-500" />
                    <div className="text-2xl font-bold">{formatCurrency(clientDetails.stats.total_spent)}</div>
                    <div className="text-xs text-gray-500">Потрачено</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <Percent className="w-6 h-6 mx-auto mb-2 text-purple-500" />
                    <div className="text-2xl font-bold">{formatCurrency(clientDetails.stats.avg_order_value)}</div>
                    <div className="text-xs text-gray-500">Средний чек</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <Calendar className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                    <div className="text-sm font-bold">{formatDate(clientDetails.stats.last_order_date)}</div>
                    <div className="text-xs text-gray-500">Последний заказ</div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Orders */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Последние заказы</Label>
                {clientDetails.recent_orders.length === 0 ? (
                  <p className="text-gray-500 text-sm">Нет заказов</p>
                ) : (
                  <div className="space-y-2">
                    {clientDetails.recent_orders.slice(0, 5).map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-mono text-sm font-medium">{order.order_number}</span>
                          <span className="text-gray-400 mx-2">•</span>
                          <span className="text-sm text-gray-500">{formatDate(order.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{order.status}</Badge>
                          <span className="font-semibold">{formatCurrency(order.total_amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Login History */}
              {clientDetails.login_history && clientDetails.login_history.length > 0 && (
                <div>
                  <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
                    <History className="w-4 h-4" />
                    История входов
                  </Label>
                  <div className="space-y-2">
                    {clientDetails.login_history.slice(0, 5).map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                        <span className="text-gray-500">{formatDate(entry.timestamp)}</span>
                        <span className="text-gray-400">{entry.device || 'Web'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* B2B Info Section */}
              {clientDetails.b2b_data && (
                <div className="p-4 border rounded-lg bg-blue-50/50">
                  <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
                    <Network className="w-4 h-4 text-blue-600" />
                    B2B Информация
                  </Label>
                  {clientDetails.b2b_data.is_owner ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Hash className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-gray-600">Код компании:</span>
                        <span className="font-mono font-semibold">{clientDetails.b2b_data.company_code || '—'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-gray-600">Сетей:</span>
                        <span className="font-semibold">{clientDetails.b2b_data.networks?.length || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-gray-600">Менеджеров:</span>
                        <span className="font-semibold">
                          {clientDetails.b2b_data.networks?.reduce((sum, n) => sum + (n.managers?.length || 0), 0) || 0}
                        </span>
                      </div>
                      <Button
                        size="sm" variant="outline"
                        className="mt-2"
                        data-testid="open-b2b-management-btn"
                        onClick={() => {
                          setShowClientDetails(false);
                          handleOpenB2BManagement(clientDetails.client);
                        }}
                      >
                        <Network className="w-3.5 h-3.5 mr-1.5" />
                        Управление B2B
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      <div><span className="text-gray-600">Роль:</span> <Badge variant="secondary">Менеджер</Badge></div>
                      <div><span className="text-gray-600">Логин:</span> <span className="font-mono">{clientDetails.b2b_data.username}</span></div>
                      <div><span className="text-gray-600">Владелец:</span> {clientDetails.b2b_data.parent_owner_name || clientDetails.b2b_data.parent_owner_email}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Редактирование клиента</DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <form onSubmit={handleSaveClient} className="flex flex-col min-h-0 flex-1">
              <div className="space-y-4 overflow-y-auto min-h-0 flex-1 px-6 py-2" data-testid="edit-client-scroll">
              <div className="p-3 bg-gray-50 rounded-lg mb-4">
                <p className="text-sm text-gray-600">Email: <strong>{selectedClient.email}</strong></p>
                <p className="text-sm text-gray-600">Тип: <strong>{selectedClient.role}</strong></p>
              </div>

              {selectedClient.role === 'B2B' ? (
                <>
                  <div>
                    <Label>Название компании</Label>
                    <Input
                      value={clientForm.company_name}
                      onChange={(e) => setClientForm(prev => ({ ...prev, company_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Контактное лицо</Label>
                    <Input
                      value={clientForm.contact_person}
                      onChange={(e) => setClientForm(prev => ({ ...prev, contact_person: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>БИН/ИИН</Label>
                    <Input
                      value={clientForm.tax_id}
                      onChange={(e) => setClientForm(prev => ({ ...prev, tax_id: e.target.value }))}
                    />
                  </div>
                  <ClientLegalEntities clientId={selectedClient.id} />
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Имя</Label>
                    <Input
                      value={clientForm.first_name}
                      onChange={(e) => setClientForm(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="Иван"
                    />
                  </div>
                  <div>
                    <Label>Фамилия</Label>
                    <Input
                      value={clientForm.last_name}
                      onChange={(e) => setClientForm(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Иванов"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label>Телефон</Label>
                <Input
                  value={clientForm.phone}
                  onChange={(e) => setClientForm(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>

              <div>
                <Label>Адрес</Label>
                <Input
                  value={clientForm.address}
                  onChange={(e) => setClientForm(prev => ({ ...prev, address: e.target.value }))}
                />
              </div>

              <div>
                <Label>Персональная скидка (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={clientForm.discount_percentage}
                  onChange={(e) => setClientForm(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))}
                />
              </div>

              <div>
                <Label>Группа скидок</Label>
                <Select
                  value={clientForm.client_group_id || 'none'}
                  onValueChange={(v) => setClientForm(prev => ({ ...prev, client_group_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger data-testid="client-group-select">
                    <SelectValue placeholder="Без группы" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Без группы</SelectItem>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Ценовой уровень</Label>
                <Select
                  value={clientForm.price_tier || 'retail'}
                  onValueChange={(v) => setClientForm(prev => ({ ...prev, price_tier: v }))}
                >
                  <SelectTrigger data-testid="price-tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retail">Розничные цены (сайт)</SelectItem>
                    <SelectItem value="wholesale">Оптовые цены (базовые)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {clientForm.price_tier === 'wholesale' ? 'Клиент видит базовые (B2B) цены' : 'Клиент видит цены сайта'}
                </p>
              </div>

              {/* Logistics priority — used by morning route builder */}
              <ClientDeliveryPoints clientId={selectedClient.id} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Приоритет доставки</Label>
                  <Select
                    value={String(clientForm.delivery_priority || 5)}
                    onValueChange={(v) => setClientForm(prev => ({ ...prev, delivery_priority: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="delivery-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">🔥 1 — VIP (везти первым)</SelectItem>
                      <SelectItem value="2">🟢 2 — Важный</SelectItem>
                      <SelectItem value="3">🟡 3 — Обычный (выше)</SelectItem>
                      <SelectItem value="4">⚪ 4 — Обычный</SelectItem>
                      <SelectItem value="5">⚪ 5 — Стандартный</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Меньше число — выше приоритет в маршруте</p>
                </div>
                <div>
                  <Label>Время разгрузки, мин</Label>
                  <Input
                    type="number"
                    min="0"
                    max="120"
                    value={clientForm.delivery_service_minutes ?? 10}
                    onChange={(e) => setClientForm(prev => ({ ...prev, delivery_service_minutes: parseInt(e.target.value || '10') }))}
                    data-testid="delivery-service-minutes-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Учитывается в расчёте ETA</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={clientForm.is_active}
                  onCheckedChange={(checked) => setClientForm(prev => ({ ...prev, is_active: checked }))}
                />
                <Label>Активен</Label>
              </div>

              <div className="p-3 bg-sky-50 border border-sky-200 rounded-md space-y-2">
                <Label className="font-medium">БИН нашей организации для 1С</Label>
                <Input
                  type="text"
                  placeholder="Пусто = ИП Кали Г.Б. (780329403060)"
                  maxLength={20}
                  value={clientForm.preferred_org_bin || ''}
                  onChange={(e) => setClientForm(prev => ({ ...prev, preferred_org_bin: e.target.value.replace(/[^0-9]/g, '') }))}
                  data-testid="preferred-org-bin-input"
                />
                <p className="text-xs text-sky-700">
                  По какой из наших ИП формировать счёт-фактуру в 1С для заказов этого клиента.<br/>
                  Оставьте <b>пустым</b> — по умолчанию <b>ИП Кали Г.Б.</b> (<code>780329403060</code>).<br/>
                  Пример: <code>870105400077</code> — ИП Айнанан. Или введите БИН ИП Кали Е.А. чтобы перевести VAT-клиентов.
                </p>
              </div>

              {selectedClient?.role === 'B2C' && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <Switch
                    checked={clientForm.postpay_allowed}
                    onCheckedChange={async (checked) => {
                      setClientForm(prev => ({ ...prev, postpay_allowed: checked }));
                      try {
                        await axios.put(
                          `${API}/admin/clients/${selectedClient.id}/postpay`,
                          { postpay_allowed: checked },
                          { headers: getAuthHeaders() }
                        );
                        toast.success(checked
                          ? '✓ Постоплата разрешена — клиент может заказывать без предоплаты'
                          : '✓ Постоплата выключена — клиент будет оплачивать через Kaspi');
                      } catch {
                        toast.error('Не удалось сохранить флаг');
                        setClientForm(prev => ({ ...prev, postpay_allowed: !checked }));
                      }
                    }}
                    data-testid="postpay-allowed-toggle"
                  />
                  <div>
                    <Label className="font-medium">Постоплата разрешена</Label>
                    <p className="text-xs text-amber-700 mt-1">
                      Доверенный клиент — заказы создаются без предоплаты через Kaspi.
                      Оставьте выключенным для обычной предоплаты.
                    </p>
                  </div>
                </div>
              )}
              </div>

              <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0 bg-white">
                <Button type="button" variant="outline" onClick={() => setShowEditClient(false)}>
                  Отмена
                </Button>
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-client-btn">
                  Сохранить
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Изменить статус клиента</DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Клиент: <strong>{
                  selectedClient.role === 'B2B'
                    ? (selectedClient.company_name || selectedClient.email)
                    : (selectedClient.first_name && selectedClient.last_name
                        ? `${selectedClient.first_name} ${selectedClient.last_name}`
                        : selectedClient.full_name || selectedClient.email)
                }</strong>
              </p>

              <div>
                <Label>Новый статус</Label>
                <Select
                  value={newStatus.status}
                  onValueChange={(v) => setNewStatus(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Причина / Примечание</Label>
                <Input
                  value={newStatus.notes}
                  onChange={(e) => setNewStatus(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Опционально..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                  Отмена
                </Button>
                <Button onClick={handleStatusChange} className="bg-emerald-700 hover:bg-emerald-800">
                  Применить
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Create Client Dialog */}
      <Dialog open={showCreateClient} onOpenChange={setShowCreateClient}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Добавить нового клиента</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newClientForm.email}
                  onChange={(e) => setNewClientForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="client@example.com"
                  data-testid="new-client-email"
                />
              </div>
              <div className="col-span-2">
                <Label>Пароль *</Label>
                <Input
                  type="text"
                  value={newClientForm.password}
                  onChange={(e) => setNewClientForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Минимум 6 символов"
                  data-testid="new-client-password"
                />
              </div>
              <div>
                <Label>Тип клиента</Label>
                <Select value={newClientForm.role} onValueChange={(v) => setNewClientForm(prev => ({ ...prev, role: v }))}>
                  <SelectTrigger data-testid="new-client-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B2C">B2C (Физлицо)</SelectItem>
                    <SelectItem value="B2B">B2B (Юрлицо)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Телефон</Label>
                <Input
                  value={newClientForm.phone}
                  onChange={(e) => setNewClientForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+7 777 123 4567"
                />
              </div>
              <div>
                <Label>Имя</Label>
                <Input
                  value={newClientForm.first_name}
                  onChange={(e) => setNewClientForm(prev => ({ ...prev, first_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Фамилия</Label>
                <Input
                  value={newClientForm.last_name}
                  onChange={(e) => setNewClientForm(prev => ({ ...prev, last_name: e.target.value }))}
                />
              </div>
              {newClientForm.role === 'B2B' && (
                <>
                  <div>
                    <Label>Компания</Label>
                    <Input
                      value={newClientForm.company_name}
                      onChange={(e) => setNewClientForm(prev => ({ ...prev, company_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>БИН/ИИН</Label>
                    <Input
                      value={newClientForm.tax_id}
                      onChange={(e) => setNewClientForm(prev => ({ ...prev, tax_id: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Контактное лицо</Label>
                    <Input
                      value={newClientForm.contact_person}
                      onChange={(e) => setNewClientForm(prev => ({ ...prev, contact_person: e.target.value }))}
                    />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <Label>Адрес</Label>
                <AddressAutocomplete
                  value={newClientForm.address}
                  onChange={(val) => setNewClientForm(prev => ({ ...prev, address: val }))}
                  onAddressSelect={(data) => setNewClientForm(prev => ({ ...prev, address: data.address || prev.address }))}
                  placeholder="Начните вводить адрес..."
                  cityCode="almaty"
                />
              </div>
              <div>
                <Label>Группа скидок</Label>
                <Select
                  value={newClientForm.client_group_id || 'none'}
                  onValueChange={(v) => setNewClientForm(prev => ({ ...prev, client_group_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Без группы" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Без группы</SelectItem>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Скидка (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={newClientForm.discount_percentage}
                  onChange={(e) => setNewClientForm(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateClient(false)}>Отмена</Button>
              <Button onClick={handleCreateClient} disabled={creatingClient} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-new-client-btn">
                {creatingClient ? 'Создание...' : 'Создать клиента'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== B2B MANAGEMENT DIALOG ==================== */}
      <Dialog open={showB2BManagement} onOpenChange={setShowB2BManagement}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="b2b-management-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="w-5 h-5 text-blue-600" />
              B2B Управление — {selectedClient?.company_name || selectedClient?.email}
            </DialogTitle>
          </DialogHeader>

          {b2bLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : b2bData ? (
            <div className="space-y-6">
              {/* Company Code */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Код компании:</span>
                    {editCompanyCode ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={companyCodeValue}
                          onChange={(e) => setCompanyCodeValue(e.target.value)}
                          className="w-40 h-8 text-sm"
                          data-testid="company-code-input"
                        />
                        <Button size="sm" onClick={handleUpdateCompanyCode} className="h-8 bg-emerald-700 hover:bg-emerald-800" data-testid="save-company-code-btn">
                          Сохранить
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditCompanyCode(false)} className="h-8">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <span className="font-mono font-semibold text-blue-700">{b2bData.company_code || 'Не задан'}</span>
                        <Button size="sm" variant="ghost" onClick={() => { setCompanyCodeValue(b2bData.company_code || ''); setEditCompanyCode(true); }} className="h-7 px-2" data-testid="edit-company-code-btn">
                          <Pencil className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => { setResetPasswordTarget({ id: b2bOwnerId, name: selectedClient?.company_name || selectedClient?.email, isOwner: true }); setShowResetPassword(true); }}
                    data-testid="reset-owner-password-btn"
                  >
                    <Lock className="w-3.5 h-3.5 mr-1.5" />
                    Сбросить пароль владельца
                  </Button>
                </div>
              </div>

              {/* Networks */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Сети ({b2bData.networks?.length || 0})
                  </h3>
                  <Button size="sm" onClick={() => setShowCreateNetwork(true)} data-testid="create-network-btn" className="bg-emerald-700 hover:bg-emerald-800">
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    Добавить сеть
                  </Button>
                </div>

                {(!b2bData.networks || b2bData.networks.length === 0) ? (
                  <p className="text-sm text-gray-500 py-4 text-center">Нет сетей</p>
                ) : (
                  <>
                  <div className="space-y-3">
                    {b2bData.networks.map((network) => (
                      <div key={network.id} className={`border rounded-lg overflow-hidden ${!network.is_active ? 'opacity-60 bg-gray-50' : ''}`} data-testid={`network-card-${network.id}`}>
                        {/* Network Header */}
                        <div
                          className="flex items-center justify-between p-3 bg-white hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleNetworkExpanded(network.id)}
                        >
                          <div className="flex items-center gap-3">
                            {expandedNetworks[network.id] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            <div>
                              <span className={`font-medium ${!network.is_active ? 'line-through text-gray-500' : ''}`}>{network.name}</span>
                              {network.description && <span className="text-xs text-gray-400 ml-2">{network.description}</span>}
                            </div>
                            {network.is_active ? (
                              <Badge variant="default" className="text-xs">Активна</Badge>
                            ) : (
                              <Badge className="text-xs bg-red-100 text-red-700 hover:bg-red-100">🔒 Закрыта</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{network.managers?.length || 0} менедж.</span>
                            <span>{network.stats?.orders_count || 0} заказов</span>
                            <span>{formatCurrency(network.stats?.total_revenue || 0)}</span>
                            <Button
                              size="sm" variant="ghost"
                              className={`h-7 px-2 ${network.is_active ? 'text-amber-600 hover:text-amber-700' : 'text-emerald-600 hover:text-emerald-700'}`}
                              onClick={(e) => { e.stopPropagation(); handleAdminToggleNetworkStatus(network); }}
                              title={network.is_active ? 'Закрыть сеть' : 'Снова открыть сеть'}
                              data-testid={`toggle-network-status-${network.id}`}
                            >
                              {network.is_active ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                            </Button>
                            <Button
                              size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700"
                              onClick={(e) => { e.stopPropagation(); handleAdminDeleteNetwork(network.id); }}
                              data-testid={`delete-network-${network.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>

                        {/* Network Expanded Content */}
                        {expandedNetworks[network.id] && (
                          <div className="border-t p-3 bg-gray-50/50">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-600">Менеджеры</span>
                              <Button
                                size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => { setCreateManagerNetworkId(network.id); setShowCreateManager(true); }}
                                data-testid={`add-manager-${network.id}`}
                              >
                                <UserPlus className="w-3 h-3 mr-1" />
                                Добавить менеджера
                              </Button>
                            </div>
                            {(!network.managers || network.managers.length === 0) ? (
                              <p className="text-xs text-gray-400 py-2">Нет менеджеров</p>
                            ) : (
                              <div className="space-y-1.5">
                                {network.managers.map((mgr) => (
                                  <div key={mgr.id} className="flex items-center justify-between p-2 bg-white rounded border text-sm" data-testid={`manager-row-${mgr.id}`}>
                                    <div className="flex items-center gap-3">
                                      <UserCircle className="w-4 h-4 text-gray-400" />
                                      <div>
                                        <span className="font-medium">{mgr.full_name}</span>
                                        <span className="text-gray-400 mx-1.5">•</span>
                                        <span className="font-mono text-xs text-gray-500">@{mgr.username}</span>
                                      </div>
                                      {mgr.phone && <span className="text-xs text-gray-400">{mgr.phone}</span>}
                                      <Badge variant={mgr.is_active ? 'default' : 'secondary'} className="text-xs">
                                        {mgr.is_active ? 'Активен' : 'Неактивен'}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm" variant="ghost" className="h-7 px-2"
                                        onClick={() => { setResetPasswordTarget({ id: mgr.id, name: mgr.full_name, isOwner: false }); setShowResetPassword(true); }}
                                        title="Сбросить пароль"
                                        data-testid={`reset-password-${mgr.id}`}
                                      >
                                        <Key className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700"
                                        onClick={() => handleAdminDeleteManager(mgr.id)}
                                        title="Деактивировать"
                                        data-testid={`delete-manager-${mgr.id}`}
                                      >
                                        <Ban className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Addresses linked to this network */}
                            <div className="flex items-center justify-between mt-4 mb-2">
                              <span className="text-sm font-medium text-gray-600">📍 Адреса доставки</span>
                            </div>
                            {(() => {
                              const linked = (b2bData.addresses || []).filter(a => a.network_id === network.id);
                              if (linked.length === 0) {
                                return <p className="text-xs text-gray-400 py-2">К этой сети не привязан ни один адрес</p>;
                              }
                              return (
                                <div className="space-y-1.5">
                                  {linked.map((a) => (
                                    <div key={a.id} className="flex items-center justify-between p-2 bg-white rounded border text-sm" data-testid={`addr-row-${a.id}`}>
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <span className="text-emerald-600">📍</span>
                                        <div className="min-w-0 flex-1">
                                          <div className="font-medium truncate">
                                            {a.label}
                                            {a.is_default && <Badge variant="default" className="ml-2 text-xs">по умолчанию</Badge>}
                                          </div>
                                          <div className="text-xs text-gray-500 truncate">{a.address_line}</div>
                                        </div>
                                      </div>
                                      <select
                                        value={a.network_id || ''}
                                        onChange={(e) => handleAdminSetAddressNetwork(a.id, e.target.value)}
                                        className="text-xs border rounded px-2 py-1 bg-white"
                                        data-testid={`addr-network-select-${a.id}`}
                                      >
                                        <option value="">— Без сети —</option>
                                        {b2bData.networks.map(n => (
                                          <option key={n.id} value={n.id}>{n.name}</option>
                                        ))}
                                      </select>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Unassigned addresses (network_id is NULL) */}
                  {(() => {
                    const unassigned = (b2bData.addresses || []).filter(a => !a.network_id);
                    if (unassigned.length === 0) return null;
                    return (
                      <div className="border rounded-lg p-3 bg-amber-50/50 border-amber-200">
                        <div className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                          ⚠️ Адреса без сети ({unassigned.length})
                          <span className="text-xs font-normal text-amber-600">— выберите сеть для каждого</span>
                        </div>
                        <div className="space-y-1.5">
                          {unassigned.map((a) => (
                            <div key={a.id} className="flex items-center justify-between p-2 bg-white rounded border text-sm" data-testid={`unassigned-addr-${a.id}`}>
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="text-emerald-600">📍</span>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium truncate">
                                    {a.label}
                                    {a.is_default && <Badge variant="default" className="ml-2 text-xs">по умолчанию</Badge>}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">{a.address_line}</div>
                                </div>
                              </div>
                              <select
                                value=""
                                onChange={(e) => e.target.value && handleAdminSetAddressNetwork(a.id, e.target.value)}
                                className="text-xs border rounded px-2 py-1 bg-white"
                                data-testid={`unassigned-network-select-${a.id}`}
                              >
                                <option value="">— Выбрать сеть —</option>
                                {b2bData.networks.map(n => (
                                  <option key={n.id} value={n.id}>{n.name}</option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  </>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Не удалось загрузить данные</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Network Dialog */}
      <Dialog open={showCreateNetwork} onOpenChange={setShowCreateNetwork}>
        <DialogContent className="max-w-md" data-testid="create-network-dialog">
          <DialogHeader>
            <DialogTitle>Новая сеть</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Название *</Label>
              <Input
                value={newNetworkForm.name}
                onChange={(e) => setNewNetworkForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Название сети"
                data-testid="network-name-input"
              />
            </div>
            <div>
              <Label>Описание</Label>
              <Input
                value={newNetworkForm.description}
                onChange={(e) => setNewNetworkForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Описание (опционально)"
                data-testid="network-description-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreateNetwork(false)}>Отмена</Button>
              <Button onClick={handleAdminCreateNetwork} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-network-btn">
                Создать
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Manager Dialog */}
      <Dialog open={showCreateManager} onOpenChange={setShowCreateManager}>
        <DialogContent className="max-w-md" data-testid="create-manager-dialog">
          <DialogHeader>
            <DialogTitle>Новый менеджер</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Логин (username) *</Label>
              <Input
                value={newManagerForm.username}
                onChange={(e) => setNewManagerForm(prev => ({ ...prev, username: e.target.value }))}
                placeholder="username"
                data-testid="manager-username-input"
              />
            </div>
            <div>
              <Label>Пароль *</Label>
              <Input
                type="text"
                value={newManagerForm.password}
                onChange={(e) => setNewManagerForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Мин. 6 символов"
                data-testid="manager-password-input"
              />
            </div>
            <div>
              <Label>ФИО *</Label>
              <Input
                value={newManagerForm.full_name}
                onChange={(e) => setNewManagerForm(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Полное имя"
                data-testid="manager-fullname-input"
              />
            </div>
            <div>
              <Label>Телефон</Label>
              <Input
                value={newManagerForm.phone}
                onChange={(e) => setNewManagerForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+7 777 123 4567"
                data-testid="manager-phone-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreateManager(false)}>Отмена</Button>
              <Button onClick={handleAdminCreateManager} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-manager-btn">
                Создать
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Password Reset Dialog (any B2B/B2C client) */}
      <Dialog open={showPwdReset} onOpenChange={(o) => { setShowPwdReset(o); if (!o) { setPwdResult(null); setPwdValue(''); } }}>
        <DialogContent className="max-w-sm" data-testid="client-pwd-reset-dialog">
          <DialogHeader>
            <DialogTitle>Сброс пароля клиента</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Клиент: <strong>{pwdTarget?.company_name || pwdTarget?.full_name || pwdTarget?.email}</strong>
              <br /><span className="text-xs text-gray-400">{pwdTarget?.email}</span>
            </p>

            {!pwdResult ? (
              <>
                <Button onClick={() => submitPwdReset(true)} disabled={pwdSaving}
                  className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="pwd-generate-btn">
                  <Key className="w-4 h-4 mr-2" /> Сгенерировать надёжный пароль
                </Button>
                <div className="text-center text-xs text-gray-400">или задать вручную</div>
                <div className="flex gap-2">
                  <Input type="text" value={pwdValue} onChange={(e) => setPwdValue(e.target.value)}
                    placeholder="Свой пароль (мин. 8, буквы+цифры)" data-testid="pwd-manual-input" />
                  <Button variant="outline" onClick={() => submitPwdReset(false)}
                    disabled={pwdSaving || !pwdValue} data-testid="pwd-manual-set-btn">Задать</Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="pwd-result">
                  <div className="text-gray-500 text-xs">Логин</div>
                  <div className="font-mono font-semibold break-all">{pwdResult.email}</div>
                  <div className="text-gray-500 text-xs mt-2">Новый пароль</div>
                  <div className="font-mono font-bold text-lg break-all text-emerald-800" data-testid="pwd-result-value">{pwdResult.password}</div>
                </div>
                <p className="text-xs text-amber-700">
                  ⚠️ Пароль показан один раз. Скопируйте и передайте клиенту (email/WhatsApp).
                </p>
                <div className="flex gap-2">
                  <Button onClick={copyPwdCredentials} className="flex-1 bg-emerald-700 hover:bg-emerald-800" data-testid="pwd-copy-btn">
                    Копировать логин и пароль
                  </Button>
                  <Button variant="outline" onClick={() => setShowPwdReset(false)}>Закрыть</Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Сброс пароля</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Пользователь: <strong>{resetPasswordTarget?.name}</strong>
            </p>
            <div>
              <Label>Новый пароль</Label>
              <Input
                type="text"
                value={resetPasswordValue}
                onChange={(e) => setResetPasswordValue(e.target.value)}
                placeholder="Мин. 6 символов"
                data-testid="new-password-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowResetPassword(false)}>Отмена</Button>
              <Button onClick={handleAdminResetPassword} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-password-btn">
                Сбросить
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full export dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-lg" data-testid="full-export-dialog">
          <DialogHeader>
            <DialogTitle>Полный экспорт клиентов</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-500">Листы в файле</Label>
              <div className="mt-2 space-y-2">
                {[
                  ['clients', 'Клиенты — контакты, БИН, постоплата, скидка, заказы, выручка'],
                  ['points', 'Точки доставки — адрес, время работы, контакт, заметка водителю'],
                  ['legal', 'Юрлица — название, БИН, юр.адрес, банк, IBAN'],
                  ['managers', 'Менеджеры клиентов — ФИО, email, телефон'],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 accent-emerald-700"
                      checked={exportBlocks[key]}
                      onChange={(e) => setExportBlocks(prev => ({ ...prev, [key]: e.target.checked }))}
                      data-testid={`export-block-${key}`}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Тип клиентов</Label>
                <Select value={exportType} onValueChange={setExportType}>
                  <SelectTrigger data-testid="export-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="b2b">Только B2B</SelectItem>
                    <SelectItem value="b2c">Только B2C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-emerald-700"
                    checked={exportActiveOnly}
                    onChange={(e) => setExportActiveOnly(e.target.checked)}
                    data-testid="export-active-only"
                  />
                  Только активные
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Отмена</Button>
            <Button onClick={handleFullExport} disabled={exporting} data-testid="full-export-submit">
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Готовим файл…' : 'Скачать Excel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Импорт B2B клиентов
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!importResult ? (
              <>
                <p className="text-sm text-gray-600">
                  Загрузите Excel-файл с B2B клиентами. Формат должен соответствовать шаблону.
                </p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="hidden"
                    id="client-import-file"
                    data-testid="client-import-file-input"
                  />
                  <label htmlFor="client-import-file" className="cursor-pointer">
                    <FileSpreadsheet className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm font-medium text-gray-700">
                      {importFile ? importFile.name : 'Нажмите для выбора файла'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Excel (.xlsx)</p>
                  </label>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <Button variant="link" size="sm" onClick={handleDownloadTemplate} className="text-emerald-700 p-0">
                    <FileDown className="w-3 h-3 mr-1" />
                    Скачать шаблон
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowImportDialog(false)}>Отмена</Button>
                    <Button onClick={handleImportClients} disabled={!importFile || importing} className="bg-emerald-700 hover:bg-emerald-800" data-testid="start-import-btn">
                      {importing ? 'Импорт...' : 'Импортировать'}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-700">{importResult.owners_created}</p>
                      <p className="text-xs text-emerald-600">B2B создано</p>
                      {importResult.owners_skipped > 0 && <p className="text-xs text-gray-400">({importResult.owners_skipped} уже были)</p>}
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700">{importResult.managers_created || 0}</p>
                      <p className="text-xs text-blue-600">Менеджеров</p>
                      {importResult.managers_skipped > 0 && <p className="text-xs text-gray-400">({importResult.managers_skipped} уже были)</p>}
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-700">{importResult.b2c_created || 0}</p>
                      <p className="text-xs text-purple-600">B2C клиентов</p>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-3 text-center col-span-3">
                      <p className="text-lg font-bold text-teal-700">
                        {importResult.networks_created} сетей / {importResult.addresses_created} адресов
                        {importResult.networks_skipped > 0 && <span className="text-sm text-gray-400 font-normal"> ({importResult.networks_skipped} сетей уже были)</span>}
                      </p>
                    </div>
                  </div>
                  {importResult.error_count > 0 && (
                    <div className="bg-red-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-red-700 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          Ошибки: {importResult.error_count} строк
                        </p>
                        {importResult.error_report_id && (
                          <Button size="sm" variant="outline" onClick={() => handleDownloadErrors(importResult.error_report_id)} className="text-red-700 border-red-300" data-testid="download-errors-btn">
                            <Download className="w-3 h-3 mr-1" />
                            Скачать ошибки
                          </Button>
                        )}
                      </div>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {importResult.error_preview?.map((err, i) => (
                          <p key={i} className="text-xs text-red-600">
                            Строка {err.row}: {err.company || err.label} — {err.error}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => { setShowImportDialog(false); setImportResult(null); setImportFile(null); }}>
                    Закрыть
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default AdminClientsPro;
